import * as React from "react";
import { cn } from "@/lib/utils";

// Native <select>-based reusable component
// Maintains the same external API previously used in Users.tsx

type SelectOption = { label: string; value: string; disabled?: boolean };

type SelectFieldProps = {
  placeholder?: string;
  options: SelectOption[];
  label?: string;
  className?: string;
  onValueChange?: (value: string) => void;
  value?: string;
  defaultValue?: string;
  id?: string;
  name?: string;
  disabled?: boolean;
};

export function SelectField({
  placeholder,
  options,
  label,
  className,
  onValueChange,
  value,
  defaultValue,
  id,
  name,
  disabled,
}: SelectFieldProps) {
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onValueChange?.(e.target.value);
  };

  return (
    <div className="w-full">
      {label ? (
        <div className="py-1.5 pl-1 pr-2 text-sm font-semibold text-foreground/80">{label}</div>
      ) : null}

      <div className="relative">
        <select
          id={id}
          name={name}
          className={cn(
            "flex h-10 w-full appearance-none rounded-md border border-input bg-background px-3 pr-8 text-sm leading-none text-foreground ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
          onChange={handleChange}
          value={value}
          defaultValue={defaultValue}
          aria-label="Select"
          disabled={disabled}
        >
          {placeholder !== undefined ? (
            <option value="" disabled={!!value} hidden>
              {placeholder}
            </option>
          ) : null}

          {options.map(opt => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
        </select>

        {/* Simple chevron indicator */}
        <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="opacity-50"
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>
      </div>
    </div>
  );
}

// Lightweight compatibility layer for shadcn/ui Select API
// Provides minimal functionality to satisfy existing imports and common use-cases

type SelectContextValue = {
  value?: string;
  setValue: (v: string) => void;
  onValueChange?: (v: string) => void;
};

const SelectContext = React.createContext<SelectContextValue | undefined>(undefined);

type BaseSelectProps = {
  value?: string;
  onValueChange?: (value: string) => void;
  defaultValue?: string;
  children?: React.ReactNode;
  disabled?: boolean;
};

export function Select({ value, onValueChange, defaultValue, children }: BaseSelectProps) {
  const [internal, setInternal] = React.useState<string | undefined>(defaultValue);
  const selected = value !== undefined ? value : internal;

  const setValue = React.useCallback(
    (v: string) => {
      setInternal(v);
      onValueChange?.(v);
    },
    [onValueChange]
  );

  return (
    <SelectContext.Provider value={{ value: selected, setValue, onValueChange }}>
      <div className="w-full">{children}</div>
    </SelectContext.Provider>
  );
}

export function SelectTrigger({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      className={cn(
        "flex h-10 w-full items-center justify-between rounded-md border px-3 text-sm",
        className
      )}
    >
      {children}
    </button>
  );
}

export function SelectValue({ placeholder }: { placeholder?: string }) {
  const ctx = React.useContext(SelectContext);
  return <span className="truncate text-left">{ctx?.value ?? placeholder ?? ""}</span>;
}

export function SelectContent({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("mt-2 w-full rounded-md border bg-background p-1 shadow-sm", className)}>
      {children}
    </div>
  );
}

export function SelectItem({ value, children }: { value: string; children: React.ReactNode }) {
  const ctx = React.useContext(SelectContext);
  if (!ctx) return <div>{children}</div>;
  return (
    <div
      role="option"
      tabIndex={0}
      className={"cursor-pointer rounded-sm px-2 py-1 text-sm hover:bg-muted"}
      onClick={() => {
        ctx.setValue(value);
      }}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          ctx.setValue(value);
        }
      }}
    >
      {children}
    </div>
  );
}
